import { AxiosError, HttpStatusCode } from 'axios';
import {
  InfiniteData,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useReducer, useMemo, useCallback, useEffect, useState } from 'react';
import { ArticleListResponse, State, apiStates } from '../types/types';
import ArticleMeta from '../types/ArticleMeta';
import { getArticles } from '../api/api';
import {
  getPrimaryCategories,
  secondaryCategories,
} from '../utilities/Categories';

interface ArticleSearchParams {
  limit?: number;
  category?: string;
  author?: string;
  orderBy?: string;
  contentType?: string[] | string;
  includeUpcoming?: boolean;
  upcoming?: boolean;
}

interface ArticlesState {
  state: State;
  apiError: AxiosError | null;
  totalArticleCount: number;
  isFetching: boolean;
  hasError: boolean;
}

type ArticlesAction =
  | { type: 'FETCHING' }
  | { type: 'ERROR'; error: AxiosError }
  | { type: 'SUCCESS'; totalCount: number }
  | { type: 'RESET_COUNT' };

function articlesReducer(
  state: ArticlesState,
  action: ArticlesAction,
): ArticlesState {
  switch (action.type) {
    case 'FETCHING':
      return {
        ...state,
        state: apiStates.FETCHING,
        apiError: null,
        isFetching: true,
        hasError: false,
      };
    case 'ERROR':
      return {
        ...state,
        state: apiStates.HAS_ERROR,
        apiError: action.error,
        totalArticleCount: 0,
        isFetching: false,
        hasError: true,
      };
    case 'SUCCESS':
      return {
        ...state,
        state: apiStates.READY,
        apiError: null,
        totalArticleCount: action.totalCount,
        isFetching: false,
        hasError: false,
      };
    case 'RESET_COUNT':
      return {
        ...state,
        totalArticleCount: 0,
        isFetching: false,
        hasError: false,
      };
    default:
      return state;
  }
}

export interface UseArticlesResult extends ArticlesState {
  articles: ArticleMeta[];
  hasNextPage: boolean;
  fetchNextPage: () => Promise<
    InfiniteQueryObserverResult<InfiniteData<ArticleListResponse>, AxiosError>
  >;
}

export function useArticles(
  searchParams: ArticleSearchParams = {},
): UseArticlesResult {
  const {
    limit,
    category,
    author,
    orderBy = 'releaseDate',
    contentType,
    includeUpcoming,
    upcoming,
  } = searchParams;

  const [state, dispatch] = useReducer(articlesReducer, {
    state: apiStates.FETCHING,
    apiError: null,
    totalArticleCount: 0,
    isFetching: true,
    hasError: false,
  });

  const queryResult = useInfiniteQuery<ArticleListResponse, AxiosError>({
    queryKey: ['articles', searchParams],
    queryFn: ({ pageParam = 1 }) =>
      getArticles({
        page: pageParam as number,
        limit,
        category,
        author,
        orderBy,
        contentType,
        includeUpcoming,
        upcoming,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.count > allPages.flatMap((page) => page.result).length) {
        return lastPage.page + 1;
      }
      return undefined;
    },
  });

  const hasNextPage = useMemo(
    () => queryResult.hasNextPage,
    [queryResult.hasNextPage],
  );
  const fetchNextPage = useCallback(
    () => queryResult.fetchNextPage(),
    [queryResult],
  );

  const [articles, setArticles] = useState<ArticleMeta[]>([]);
  useEffect(() => {
    const rawArticles =
      queryResult.data?.pages.flatMap((page) => page.result) ?? [];
    setArticles(rawArticles.map(ArticleMeta.fromApiArticleListItem));
  }, [queryResult.data]);

  useEffect(() => {
    if (queryResult.isFetching && !queryResult.isFetchingNextPage) {
      dispatch({ type: 'FETCHING' });
    } else if (queryResult.error) {
      if (
        queryResult.error.response?.status === HttpStatusCode.NotFound &&
        category &&
        (getPrimaryCategories().includes(category) ||
          Object.values(secondaryCategories).includes(category))
      ) {
        dispatch({ type: 'RESET_COUNT' });
      } else {
        dispatch({ type: 'ERROR', error: queryResult.error });
      }
    } else if (queryResult.fetchStatus === 'idle' && !queryResult.isError) {
      if (!queryResult.data) {
        dispatch({ type: 'ERROR', error: new AxiosError('No data received') });
      } else {
        dispatch({
          type: 'SUCCESS',
          totalCount:
            queryResult.data.pages[queryResult.data.pages.length - 1].count,
        });
      }
    } else {
      return;
    }
  }, [
    queryResult.isFetching,
    queryResult.isFetchingNextPage,
    queryResult.error,
    queryResult.fetchStatus,
    queryResult.isError,
    queryResult.data,
    category,
  ]);

  return {
    ...state,
    articles,
    hasNextPage,
    fetchNextPage,
  };
}
